import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useApiMutation } from '../useApiMutation';
import { contactKeys, invoiceKeys, jobKeys, paymentKeys } from '@/lib/queryKeys';
import { billingV2Endpoints } from '@/lib/api/endpoints';
import { billingV2Post } from '@/lib/api/clients/billing-v2';

export interface ApplyCreditInput {
  contactId: string;
  invoiceId: string;
  amount: number;
  /** Restrict the source to a single unapplied payment (optional). */
  paymentId?: string;
}

export interface ApplyCreditResult {
  invoiceId: string;
  jobId: string | null;
  amountApplied: number;
  creditRemaining: number;
  invoiceAmountDue: number;
  invoiceAmountPaid: number;
  consumedPaymentIds: string[];
}

/**
 * Apply a customer's available credit (their unapplied payment balance — NOT a
 * credit memo) to an open invoice. On success refreshes the invoice detail +
 * payments, the contact's credit tile / balance, and the payment lists.
 */
export function useApplyCredit(contactId: string) {
  const queryClient = useQueryClient();

  return useApiMutation<ApplyCreditResult, ApplyCreditInput>({
    mutationFn: (input) =>
      billingV2Post<ApplyCreditResult, ApplyCreditInput>(
        billingV2Endpoints.paymentApplyCredit(),
        input,
      ),
    onSuccess: (result) => {
      void queryClient.invalidateQueries({
        queryKey: invoiceKeys.detail(result.invoiceId),
      });
      void queryClient.invalidateQueries({ queryKey: invoiceKeys.lists() });
      // Credit tile + outstanding balance on the contact.
      void queryClient.invalidateQueries({
        queryKey: contactKeys.summary(contactId),
      });
      void queryClient.invalidateQueries({
        queryKey: contactKeys.paymentsBase(contactId),
      });
      void queryClient.invalidateQueries({ queryKey: paymentKeys.lists() });
      if (result.jobId) {
        void queryClient.invalidateQueries({ queryKey: invoiceKeys.byJob(result.jobId) });
        void queryClient.invalidateQueries({ queryKey: jobKeys.payments(result.jobId) });
        void queryClient.invalidateQueries({ queryKey: jobKeys.financialSummary(result.jobId) });
      }
      toast.success(
        `Applied $${result.amountApplied.toFixed(2)} credit — $${result.creditRemaining.toFixed(2)} remaining`,
      );
    },
  });
}
